import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PaginationRequest,
  PerformanceFilter,
  Product,
  ProductDetails,
  ProductDetailsSections,
  ProductPerformanceList,
  Wallet,
  WalletSummary,
} from '../../../../model';
import { ProductUnderlyingGroupsPaginated } from '../../../../model/wallet/details/product-underlying-groups-paginated';
import {
  addAllWalletBasicExtraReducers,
  addWalletPendingAndErrorExtraReducers,
} from '../../../../utils/action-builder-utils';
import { defaultPagination } from '../../../../utils';
import { getProductMacroCategory } from '../../../../utils';
import {
  downloadWalletFile,
  retrieveProductsPerformances,
  retrieveProductTransactions,
  retrieveProductUnderlying,
  retrieveWallet,
} from './my-wallet-thunk';
import {
  MyWalletSections,
  MyWalletState,
  WalletPayloadAction,
} from './my-wallet-types';

const checkSharedAccountsSection = <T>(
  action: PayloadAction<WalletPayloadAction<T>>
): boolean => action.payload.selectedSection === 'SHARED_ACCOUNTS';

const checkSharedAccountsOrPirBoxSection = <T>(
  action: PayloadAction<WalletPayloadAction<T>>
): boolean =>
  action.payload.selectedSection === 'SHARED_ACCOUNTS' ||
  action.payload.selectedSection === 'PIR_BOX';

const initialPerformanceFilter: PerformanceFilter = {
  macroCategory: 'Managed',
  dateType: 'START_YEAR',
  showClosedPositions: true,
};

const initialProductDetails: ProductDetails = {
  contract: null,
  category: null,
  transactionsPaginated: {
    items: {},
    pageInfo: defaultPagination,
  },
  underlyingGroups: {
    type: null,
    underlyingGroupsPaginated: {
      items: {},
      pageInfo: defaultPagination,
    },
  },
};

const initialState: MyWalletState = {
  wallet: null,
  flagOpenProductDetails: false,
  flagOpenDateFilter: false,
  productDetails: initialProductDetails,
  productDetailsSelectedSection: 'TRANSACTIONS',
  performanceFilter: initialPerformanceFilter,
  selectedProduct: null,
  summary: null,
  walletRequestStatus: 'idle',
  productsPerformancesRequestStatus: 'idle',
  productTransactionsRequestStatus: 'idle',
  productUnderlyingRequestStatus: 'idle',
  fileDownloadedRequestStatus: 'idle',
  flagShowBalance: false,
  portfolioSummary: undefined,
  portfolioSummaryRequestStatus: 'idle',
  realEconomyAssetSummary: undefined,
  realEconomyAssetSummaryRequestStatus: 'idle',
  realEconomyResidueDuration: null,
  realEconomyResidueDurationRequestStatus: 'idle',
  realEconomyRiskProfile: null,
  realEconomyRiskProfileRequestStatus: 'idle',
  realEconomyProductsConcentration: undefined,
  realEconomyProductsConcentrationRequestStatus: 'idle',
  myWalletSection: 'POSITION_LIST',
};

const sharedAccountsSlice = createSlice({
  name: 'features/walletStore/sharedAccounts',
  initialState: () => {
    return initialState;
  },
  reducers: {
    resetWalletState() {
      return initialState;
    },
    clearWalletError(state) {
      state.walletError = undefined;
      state.walletRequestStatus = 'idle';
    },
    clearProductTransactionsError(state) {
      state.productTransactionsError = undefined;
      state.productTransactionsRequestStatus = 'idle';
    },
    clearProductUnderlyingError(state) {
      state.productUnderlyingError = undefined;
      state.productUnderlyingRequestStatus = 'idle';
    },
    clearProductsPerformancesError(state) {
      state.productsPerformancesError = undefined;
      state.productsPerformancesRequestStatus = 'idle';
    },
    setSelectedProduct(state, action: PayloadAction<Product>) {
      const macroCategory = getProductMacroCategory(
        state.wallet,
        action.payload.isin
      );
      if (macroCategory !== 'TraditionalInsurance') {
        state.selectedProduct = action.payload;
        state.flagOpenProductDetails = true;
        state.productDetails = {
          ...initialProductDetails,
          contract: action.payload.contract,
          category: macroCategory,
        };
      }
    },
    setMyWalletSection(state, action: PayloadAction<MyWalletSections>) {
      state.myWalletSection = action.payload;
    },
    clearSelectedProduct(state) {
      state.selectedProduct = null;
      state.flagOpenProductDetails = false;
      state.productDetails = initialProductDetails;
    },
    setDateFilterOpened(state, action: PayloadAction<boolean>) {
      state.flagOpenDateFilter = action.payload;
    },
    setProductDetailsSelectedSection(
      state,
      action: PayloadAction<ProductDetailsSections>
    ) {
      state.productDetailsSelectedSection = action.payload;
    },
    clearProductDetails(state) {
      state.productDetails = initialProductDetails;
      state.productDetailsSelectedSection = 'TRANSACTIONS';
    },
    setPerformanceFilter(state, action: PayloadAction<PerformanceFilter>) {
      state.performanceFilter = action.payload;
    },
    updateFlagShowBalance(state, action: PayloadAction<boolean>) {
      state.flagShowBalance = action.payload;
    },
    setPageTransactions(state, action: PayloadAction<PaginationRequest>) {
      state.productDetails.transactionsPaginated.pageInfo = {
        ...state.productDetails.transactionsPaginated.pageInfo,
        ...action.payload,
      };
    },
    setPageUnderlying(state, action: PayloadAction<PaginationRequest>) {
      state.productDetails.underlyingGroups.underlyingGroupsPaginated.pageInfo =
        {
          ...state.productDetails.underlyingGroups.underlyingGroupsPaginated
            .pageInfo,
          ...action.payload,
        };
    },
    clearFileDownloadedError(state) {
      state.fileDownloadedError = undefined;
      state.fileDownloadedRequestStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    addWalletPendingAndErrorExtraReducers(
      builder,
      retrieveWallet,
      'wallet',
      checkSharedAccountsSection
    );
    builder.addCase(
      retrieveWallet.fulfilled,
      (
        state,
        action: PayloadAction<
          WalletPayloadAction<{ wallet: Wallet; summary: WalletSummary }>
        >
      ) => {
        if (checkSharedAccountsSection(action)) {
          state.walletRequestStatus = 'succeeded';
          state.wallet = action.payload.payload.wallet;
          state.summary = action.payload.payload.summary;
        }
      }
    );
    builder.addCase(
      retrieveProductsPerformances.fulfilled,
      (
        state,
        action: PayloadAction<WalletPayloadAction<ProductPerformanceList>>
      ) => {
        if (checkSharedAccountsSection(action)) {
          state.wallet = {
            ...state.wallet,
            performances: action.payload.payload,
          };
          state.productsPerformancesRequestStatus = 'succeeded';
        }
      }
    );
    addWalletPendingAndErrorExtraReducers(
      builder,
      retrieveProductsPerformances,
      'productsPerformances',
      checkSharedAccountsSection
    );
    builder.addCase(
      retrieveProductTransactions.fulfilled,
      (
        state,
        action: PayloadAction<
          WalletPayloadAction<
            Omit<ProductDetails, 'contract' | 'category' | 'underlyingGroups'>
          >
        >
      ) => {
        if (checkSharedAccountsSection(action)) {
          state.productDetails = {
            ...state.productDetails,
            ...action.payload.payload,
          };
          state.productTransactionsRequestStatus = 'succeeded';
        }
      }
    );
    addWalletPendingAndErrorExtraReducers(
      builder,
      retrieveProductTransactions,
      'productTransactions',
      checkSharedAccountsSection
    );
    builder.addCase(
      retrieveProductUnderlying.fulfilled,
      (
        state,
        action: PayloadAction<
          WalletPayloadAction<ProductUnderlyingGroupsPaginated>
        >
      ) => {
        if (checkSharedAccountsSection(action)) {
          state.productDetails = {
            ...state.productDetails,
            underlyingGroups: action.payload.payload,
          };
          state.productUnderlyingRequestStatus = 'succeeded';
        }
      }
    );
    addWalletPendingAndErrorExtraReducers(
      builder,
      retrieveProductUnderlying,
      'productUnderlying',
      checkSharedAccountsSection
    );
    addAllWalletBasicExtraReducers(
      builder,
      downloadWalletFile,
      'fileDownloaded',
      checkSharedAccountsOrPirBoxSection
    );
  },
});

export const sharedAccountsSliceActions = sharedAccountsSlice.actions;

export default sharedAccountsSlice.reducer;
